// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import breakpoints from "@conklin/XDL/PreStyle/breakpoints";
import systemZIndex from "@conklin/XDL/PreStyle/zIndex";
import { createLinkIconStyles } from "@conklin/XDL/Utilities/sharedStyles";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import Edit from "@insite/mobius/Icons/Edit";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
// eslint-disable-next-line spire/fenced-imports
import { TS } from "@insite/shell/TS";
import isDate from "lodash/isDate";

/**
 * To load JS script with callback.
 * you can pass one url or multiple
 * Example:
 * getScripts.urls([ // url1, url2]).then(() => { //callback handler });
 */
export const getScripts = {
    url: (url: string) => {
        return new Promise((resolve, reject) => {
            if (typeof document !== "undefined") {
                const script = document.createElement("script");
                script.type = "text/javascript";
                script.src = url;
                script.addEventListener("load", () => resolve(script), false);
                script.addEventListener("error", () => reject(script), false);
                document.body.appendChild(script);
            }
        });
    },
    urls: (urls: string[]) => {
        return Promise.all(urls.map(getScripts.url));
    },
};

export const isMobileScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;

export const isTabletScreen = () => typeof window !== "undefined" && window.matchMedia("(max-width: 1024px)").matches;

export const isWideScreen = () => typeof window !== "undefined" && window.matchMedia("(min-width: 2200px)").matches;

/**
 * To convert string to boolean
 * @param value
 */
export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * split without empty or undefined values
 * @param value
 */
export const flatSplit = (value?: string, target = "") => (value || "").split(target).filter(item => item) || [];

/**
 * Method use to pass for props not accept undefined
 */
export const doNothing = () => {};

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};

export const getSystemZIndex = (key: string): number => {
    const zIndexValues = systemZIndex as any;
    return zIndexValues[key] || -1;
};

export const formatDate = (date: Date | null) => {
    if (!date) {
        return "";
    }

    let dt = date;
    if (!isDate(dt)) {
        dt = new Date(dt);
    }

    return getLocalizedDateTime({
        dateTime: dt,
        options: {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        },
    });
};

export const createToggleLinkStyles = (open?: boolean, hasNotes?: boolean) => {
    if (open) {
        return createLinkIconStyles(Minus, "small", "left", false, {
            icon: {
                iconProps: {
                    ...({ width: 11, height: 13 } as any),
                },
            },
        });
    }

    return createLinkIconStyles(hasNotes ? Edit : Plus, "small", "left", false, {
        icon: {
            iconProps: {
                ...({ width: 11, height: 13 } as any),
            },
        },
    });
};

export const generateSlidesPerViewMap = (opts?: { [key: number]: number }) => {
    const { maxWidths } = breakpoints;
    const values: any = {};
    values[maxWidths[0]] = {
        slidesPerView: opts?.[0] || 1,
    };
    values[maxWidths[1]] = {
        slidesPerView: opts?.[1] || 2,
    };
    values[maxWidths[2]] = {
        slidesPerView: opts?.[2] || 3,
    };
    values[maxWidths[3]] = {
        slidesPerView: opts?.[3] || 4,
    };
    return values;
};

export const safeJsonParse = (str: string) => {
    try {
        return JSON.parse(str);
    } catch {
        return str;
    }
};

export const getVideoEmbeddedUrl = (src: string) => {
    if (!src) {
        return;
    }
    if (src.includes("vimeo")) {
        return src;
    }
    const srcParts = src.split("/");
    const videoId = srcParts.pop();
    return `https://www.youtube.com/embed/${videoId}`;
};


export const preloadImageWithPromise = (src: string) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve(img);
        img.onerror = reject;
    });
};

export const getYoutubeThumbnail = (url: string) => {
    // This regex matches all youtube urls ( embed, shortlinks, full-url )
    if (!url) {
        return "";
    }
    const match = url.match(
        /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|shorts\/|embed\/|v\/)?)([\w\-]+)(\S+)?$/,
    );

    if (!match?.length) {
        return "";
    }
    const videoId = match[6];
    return `https://img.youtube.com/vi/${videoId}/default.jpg`;
};

export const validateCssLength = (value: string) => {
    if (value === "") {
        return "";
    }
    const lengthRegex = /[\d]+([a-zA-Z]+|%)/gi;
    if (!lengthRegex.test(value) || value === "0") {
        return TS("Value must use a valid CSS length format (e.g. 10px or 30%)");
    }
    return null;
};

export const getImageKitSrc = (src: string, width: string | number, height: string | number) => {
    return `${src}${src.indexOf("?") > -1 ? "&" : "?"}tr=w-${width || "auto"},h-${
        height || "auto"
    },cm-pad_resize,bg-FFFFFF`;
};
